import {
  Grid,
  IconButton,
  LinearProgress,
  Card,
  Chip,
  CardContent,
  CardHeader,
  Tooltip,
  Stack,
  TextField,
  MenuItem,
  Typography,
  Link,
} from "@mui/material";
import { GridPagination } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import ResponsiveDataGrid from "../../../components/MobileList/ResponsiveDataGrid";
import { MdOutlineClear, MdAttachMoney } from "react-icons/md";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { QiTransfer, setSearchBody } from "../../../app/slices/qiTransferSlice";
import dayjs from "dayjs";
import DateRangepicker from "../../../helpers/dateRangePicker";
import { restoreFilters } from "../../../helpers/restoreFilters";
import PosForm from "../../POS/form";
import { PointOfSales } from "../../../app/slices/posSlice";
import { SelectPos } from "../../../components/Selectors/SelectPos";

const chipColors = { 1: "info", 2: "warning", 3: "success", 4: "error" };

const defaultSearch = {
  qi_status_id: "",
  pos: null,
  payment_id: "",
  start_date: null,
  end_date: null,
};

const QiTransferPage = ({ userId }) => {
  const dispatch = useDispatch();

  const _QiTransfer = useSelector((state) => state.qiTransfer.qiTransfer);
  const _SearchBody = useSelector((state) => state.qiTransfer.searchBody);
  const _PosDialog = useSelector((state) => state.pointOfsales.pos.dialog);
  const statuses = useSelector((state) => state.qiTransfer.statuses);

  const _PointOfsales = useSelector(
    (state) => state.pointOfsales.posesList.data,
  );
  const [search, setSearch] = useState(() => ({
    ...restoreFilters(defaultSearch, _SearchBody),
    pos: _PointOfsales?.find((x) => x.user_id === _SearchBody.user_id) ?? null,
  }));
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);

  // ponytail: row field names are guessed until the backend shares the response shape
  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.3,
    },
    {
      field: "id",
      headerName: "رقم العملية",
      flex: 0.5,
    },
    {
      field: "pos_name",
      headerName: "نقطة البيع",
      flex: 1,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => dispatch(PointOfSales.getById(params.row.pos_id))}
        >
          {params.row.user_name}
        </Link>
      ),
    },
    {
      field: "amount",
      headerName: "المبلغ (IQD)",
      flex: 1,
      renderCell: (params) => params.row.amount?.toLocaleString(),
    },
    {
      field: "payment_id",
      headerName: "رقم الدفع",
      flex: 1,
    },
    {
      field: "qi_status_id",
      headerName: "الحالة",
      flex: 1,
      renderCell: (params) => (
        <Chip
          size="small"
          color={chipColors[params.row.qi_status_id] ?? "default"}
          label={
            statuses.find((x) => x.id === params.row.qi_status_id)?.name ??
            params.row.qi_status_name
          }
        />
      ),
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) => (
        <Stack>
          <Typography>
            {dayjs(params.row.created_at).format("YYYY-MM-DD")}
          </Typography>
          <Typography variant="caption">
            {dayjs(params.row.created_at).format("hh:mm a")}
          </Typography>
        </Stack>
      ),
    },
  ];

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي : {_QiTransfer.total_amount?.toLocaleString()} (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  useEffect(() => {
    dispatch(QiTransfer.getStatuses());
  }, [dispatch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(QiTransfer.getAll(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
  }, [_SearchBody, dispatch]);

  useEffect(() => {
    dispatch(
      setSearchBody({
        qi_status_id: search.qi_status_id,
        user_id: userId ?? search.pos?.user_id ?? "",
        payment_id: search.payment_id,
        start_date: search.start_date ?? "",
        end_date: search.end_date ?? "",
        skip: currentPage * pageSize,
        take: pageSize,
      }),
    );
  }, [currentPage, dispatch, pageSize, userId, search]);

  return (
    <Grid container spacing={2}>
      {/* the wallet page already renders PosForm */}
      {!userId && _PosDialog && <PosForm inNeedReload={false} />}
      {!userId && (
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <Typography variant="h4" component="div">
                  معاملات Qi
                </Typography>
              }
              avatar={<MdAttachMoney size={20} />}
            />
          </Card>
        </Grid>
      )}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  size="small"
                  label="الحالة"
                  value={search.qi_status_id}
                  onChange={(e) => {
                    setSearch({ ...search, qi_status_id: e.target.value });
                    setCurrentPage(0);
                  }}
                  fullWidth
                >
                  <MenuItem value="">الكل</MenuItem>
                  {statuses.map((x) => (
                    <MenuItem key={x.id} value={x.id}>
                      {x.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {!userId && (
                <Grid item xs={12} md={3}>
                  <SelectPos
                    value={search.pos}
                    onChange={(e, newValue) => {
                      setSearch({ ...search, pos: newValue ?? null });
                      setCurrentPage(0);
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  label="رقم الدفع"
                  value={search.payment_id}
                  onChange={(e) => {
                    setSearch({ ...search, payment_id: e.target.value });
                    setCurrentPage(0);
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DateRangepicker
                  startDate={search.start_date}
                  endDate={search.end_date}
                  handleOnChange={(searchDate) => {
                    setSearch({
                      ...search,
                      start_date: dayjs(searchDate[0].startDate).format(
                        "YYYY-MM-DD",
                      ),
                      end_date: dayjs(searchDate[0].endDate).format(
                        "YYYY-MM-DD",
                      ),
                    });
                    setCurrentPage(0);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Tooltip title="الغاء الفلتر">
                  <IconButton
                    onClick={() => {
                      setSearch({ ...defaultSearch });
                      setPageSize(15);
                      setCurrentPage(0);
                    }}
                  >
                    <MdOutlineClear />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ height: { xs: "auto", md: "70vh" }, width: "100%" }}>
                  <ResponsiveDataGrid
                    gridHeight={null}
                    rows={(_QiTransfer.data ?? []).map((x, index) => ({
                      ...x,
                      index: currentPage * pageSize + index + 1,
                    }))}
                    columns={columns}
                    pageSize={pageSize}
                    page={currentPage}
                    rowCount={_QiTransfer.total}
                    loading={_QiTransfer.loading}
                    rowHeight={50}
                    pagination
                    paginationMode="server"
                    selectionModel={[]}
                    onPageChange={(newPage) => setCurrentPage(newPage)}
                    rowsPerPageOptions={[15, 25, 50, 100]}
                    onPageSizeChange={(newPageSize) => {
                      setPageSize(newPageSize);
                    }}
                    components={{
                      LoadingOverlay: LinearProgress,
                      Footer: CustomFooter,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default QiTransferPage;
